import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Upload, X } from 'lucide-react';
import { chileCommunes, chileRegions, normalizeChileRegion } from '../../config/chileRegions';
import {
  ORGANIZATION_SEGMENT_OPTIONS,
  parseOrganizationChannels,
  serializeOrganizationChannels,
} from '../forms/OrganizationsForm';
import {
  getActiveChannelOptions,
  getActiveIndustryOptions,
  getActiveSubindustryOptions,
} from './commercialCatalogDefaults';
import { useBusinessUnit } from '../../contexts/BusinessUnitContext';

const TEMPLATE_HEADERS = [
  'org_name',
  'rut',
  'segment',
  'channel',
  'industry',
  'subindustry',
  'email',
  'phone',
  'address',
  'commune',
  'region',
  'country',
  'website',
  'linkedin',
  'created_by',
];

const COLUMN_LABELS = {
  org_name: 'Nombre',
  rut: 'RUT',
  segment: 'Segmento',
  channel: 'Canal',
  industry: 'Industria',
  subindustry: 'Subindustria',
  email: 'Email',
  phone: 'Teléfono',
  address: 'Dirección',
  commune: 'Comuna',
  region: 'Región',
  country: 'País',
  website: 'Web',
  linkedin: 'LinkedIn',
  created_by: 'Creado por',
};

const ALLOWED_SEGMENTS = new Set(ORGANIZATION_SEGMENT_OPTIONS.map((o) => o.value));

const foldCatalogKey = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const COUNTRY_OPTIONS = [
  { value: 'Chile', label: 'Chile' },
  { value: 'Perú', label: 'Perú' },
  { value: 'Argentina', label: 'Argentina' },
  { value: 'Bolivia', label: 'Bolivia' },
  { value: 'Colombia', label: 'Colombia' },
  { value: 'México', label: 'México' },
  { value: 'Otro', label: 'Otro' },
];

const ALL_COMMUNE_OPTIONS = Object.values(chileCommunes)
  .flat()
  .map((c) => ({ value: c.value, label: c.label || c.value }));

const COMMUNE_TO_REGION = Object.entries(chileCommunes).reduce((acc, [region, communes]) => {
  (communes || []).forEach((commune) => {
    const key = foldCatalogKey(commune?.value || '');
    if (key) acc[key] = region;
  });
  return acc;
}, {});

const INDUSTRY_ALIASES = {
  mantenimiento: 'Montaje y Mantención',
  mantencion: 'Montaje y Mantención',
  'montaje y mantencion': 'Montaje y Mantención',
  'montaje y mantenimiento': 'Montaje y Mantención',
  construccion: 'Construcción',
  'administracion publica': 'Administración Pública',
  mineria: 'Minería',
  logistica: 'Logística',
  energia: 'Energía',
  educacion: 'Educación',
};

const TEMPLATE_EXAMPLE_CELLS = [
  'Empresa Ejemplo SpA',
  '76.123.456-0',
  'B2B',
  'Directo',
  'Construcción',
  'Pinturas',
  'contacto@ejemplo.cl',
  '+56912345678',
  'Av. Ejemplo 100',
  'Santiago',
  'Metropolitana',
  'Chile',
  '',
  '',
  'sistema',
];

const TEMPLATE_EXAMPLE = TEMPLATE_EXAMPLE_CELLS.join(';');

const emptyRow = () => ({
  org_name: '',
  rut: '',
  segment: '',
  channel: '',
  industry: '',
  subindustry: '',
  email: '',
  phone: '',
  address: '',
  commune: '',
  region: '',
  country: '',
  website: '',
  linkedin: '',
  created_by: '',
});

const escapeXml = (value = '') =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Plantilla Excel (.xls SpreadsheetML) compatible con Excel / LibreOffice. */
export const downloadOrganizationsExcelTemplate = () => {
  const headerCells = TEMPLATE_HEADERS.map(
    (header) => `<Cell><Data ss:Type="String">${escapeXml(header)}</Data></Cell>`
  ).join('');
  const exampleCells = TEMPLATE_EXAMPLE_CELLS.map(
    (cell) => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`
  ).join('');
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Worksheet ss:Name="Organizaciones">
  <Table>
   <Row>${headerCells}</Row>
   <Row>${exampleCells}</Row>
  </Table>
 </Worksheet>
</Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'plantilla_organizaciones.xls';
  a.click();
  URL.revokeObjectURL(url);
};

const normalizeHeader = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

const HEADER_ALIASES = {
  org_name: ['org_name', 'nombre', 'organizacion', 'empresa', 'razon_social'],
  rut: ['rut', 'tax_id', 'id'],
  segment: ['segment', 'segmento'],
  channel: ['channel', 'canal'],
  industry: ['industry', 'industria'],
  subindustry: ['subindustry', 'subindustria'],
  email: ['email', 'correo', 'mail'],
  phone: ['phone', 'fono', 'telefono', 'tel'],
  address: ['address', 'direccion'],
  commune: ['commune', 'comuna'],
  region: ['region'],
  country: ['country', 'pais'],
  website: ['website', 'web', 'sitio'],
  linkedin: ['linkedin'],
  created_by: ['created_by', 'creado_por', 'responsable'],
};

const detectDelimiter = (line = '') => {
  const commas = (line.match(/,/g) || []).length;
  const semis = (line.match(/;/g) || []).length;
  const tabs = (line.match(/\t/g) || []).length;
  if (tabs >= commas && tabs >= semis && tabs > 0) return '\t';
  if (semis >= commas) return ';';
  return ',';
};

const splitCsvLine = (line, delimiter) => {
  const cells = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());
  return cells;
};

const mapHeaderToField = (header) => {
  const key = normalizeHeader(header);
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.includes(key)) return field;
  }
  return TEMPLATE_HEADERS.includes(key) ? key : null;
};

const unescapeXml = (value = '') =>
  String(value || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');

const looksLikeSpreadsheetMl = (text = '') => {
  const sample = String(text || '').slice(0, 4000).toLowerCase();
  return (
    sample.includes('mso-application') ||
    sample.includes('urn:schemas-microsoft-com:office:spreadsheet') ||
    (sample.includes('<workbook') && sample.includes('<worksheet'))
  );
};

const looksLikeBinaryOffice = (text = '') => {
  const sample = String(text || '');
  if (!sample) return false;
  if (sample.charCodeAt(0) === 0x50 && sample.charCodeAt(1) === 0x4b) return true;
  const nulls = [...sample.slice(0, 2000)].filter((ch) => ch.charCodeAt(0) === 0).length;
  return nulls > 20;
};

const decodeBasicHtmlEntities = (value = '') =>
  String(value || '')
    .replace(/&#160;/gi, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#(\d+);/g, (_, code) => {
      const n = Number(code);
      return Number.isFinite(n) ? String.fromCharCode(n) : '';
    })
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");

/** Extrae filas de celdas desde SpreadsheetML (plantilla .xls XML). */
export const parseSpreadsheetMlRows = (rawText = '') => {
  const text = String(rawText || '');
  const rowBlocks = text.match(/<Row\b[^>]*>[\s\S]*?<\/Row>/gi) || [];
  const table = [];
  rowBlocks.forEach((rowXml) => {
    const cells = [];
    const cellRegex = /<Cell\b([^>]*)>([\s\S]*?)<\/Cell>|<Cell\b([^>]*)\/>/gi;
    let match;
    let nextIndex = 1;
    while ((match = cellRegex.exec(rowXml)) !== null) {
      const attrs = `${match[1] || ''}${match[3] || ''}`;
      const inner = match[2] || '';
      const indexMatch = attrs.match(/\bss:Index\s*=\s*["'](\d+)["']/i);
      if (indexMatch) {
        nextIndex = Number(indexMatch[1]);
      }
      while (cells.length < nextIndex - 1) cells.push('');
      const dataMatch = inner.match(/<Data\b[^>]*>([\s\S]*?)<\/Data>/i);
      const value = dataMatch ? unescapeXml(dataMatch[1]).trim() : '';
      cells[nextIndex - 1] = value;
      nextIndex += 1;
    }
    for (let i = 0; i < cells.length; i += 1) {
      if (cells[i] == null) cells[i] = '';
    }
    if (cells.some((c) => String(c || '').trim())) {
      table.push(cells);
    }
  });
  return table;
};

const buildRowsFromCellTable = (table) => {
  if (!table.length) {
    return { rows: [], error: 'No se encontraron filas con datos en el Excel.' };
  }

  const headerCells = table[0].map((c) => String(c || '').trim());
  const mappedHeaders = headerCells.map(mapHeaderToField);
  const hasOrgNameHeader = mappedHeaders.includes('org_name');

  let startIndex = 0;
  let fieldOrder = mappedHeaders;
  if (!hasOrgNameHeader) {
    fieldOrder = TEMPLATE_HEADERS.slice(0, headerCells.length);
    startIndex = 0;
  } else {
    startIndex = 1;
  }

  if (!fieldOrder.includes('org_name') && !hasOrgNameHeader && fieldOrder[0] !== 'org_name') {
    return {
      rows: [],
      error: 'La primera columna debe ser org_name (o incluye cabecera con nombre/organización).',
    };
  }

  const rows = [];
  for (let i = startIndex; i < table.length; i += 1) {
    const cells = table[i];
    const row = emptyRow();
    fieldOrder.forEach((field, idx) => {
      if (!field) return;
      row[field] = decodeBasicHtmlEntities(String(cells[idx] || '')).trim();
    });
    if (!row.org_name && !hasOrgNameHeader) {
      row.org_name = decodeBasicHtmlEntities(String(cells[0] || '')).trim();
    }
    if (!row.org_name || /^[<>/?]/.test(row.org_name) || /<\/?[A-Za-z]/.test(row.org_name)) {
      continue;
    }
    rows.push(row);
  }

  if (!rows.length) {
    return { rows: [], error: 'No se encontraron filas con nombre de organización.' };
  }
  return { rows, error: '' };
};

export const parseOrganizationsCsv = (rawText = '') => {
  const text = String(rawText || '').replace(/^\uFEFF/, '').trim();
  if (!text) return { rows: [], error: 'Pega o sube un archivo CSV o la plantilla Excel (.xls).' };

  if (looksLikeBinaryOffice(text)) {
    return {
      rows: [],
      error:
        'Este archivo parece .xlsx/.xls binario. Usa la plantilla Excel descargada aquí, o exporta a CSV (separado por ;).',
    };
  }

  if (looksLikeSpreadsheetMl(text)) {
    return buildRowsFromCellTable(parseSpreadsheetMlRows(text));
  }

  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (!lines.length) return { rows: [], error: 'El archivo está vacío.' };

  if (/^<\?xml/i.test(lines[0]) || /^<\?mso-/i.test(lines[0]) || /^<\/?[A-Za-z]/.test(lines[0])) {
    return {
      rows: [],
      error:
        'El archivo parece XML/Excel sin formato válido. Descarga la plantilla o guarda como CSV e inténtalo de nuevo.',
    };
  }

  const delimiter = detectDelimiter(lines[0]);
  const table = lines.map((line) => splitCsvLine(line, delimiter));
  return buildRowsFromCellTable(table);
};

const cleanRut = (value = '') =>
  decodeBasicHtmlEntities(value)
    .toUpperCase()
    .replace(/[^0-9K]/g, '');

/** Formato visual esperado: 12.345.678-9 o 1.234.567-K */
const isRutDisplayFormat = (value = '') =>
  /^\d{1,2}\.\d{3}\.\d{3}-[\dK]$/i.test(String(value || '').trim());

const isValidChileanRut = (value = '') => {
  const clean = cleanRut(value);
  if (clean.length < 2) return false;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);
  if (!/^\d{7,8}$/.test(body)) return false;
  if (!/^[0-9K]$/.test(dv)) return false;
  let sum = 0;
  let mul = 2;
  for (let i = body.length - 1; i >= 0; i -= 1) {
    sum += Number(body[i]) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }
  const mod = 11 - (sum % 11);
  const expected = mod === 11 ? '0' : mod === 10 ? 'K' : String(mod);
  return dv === expected;
};

const normalizeSegmentValue = (raw = '') => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const compact = s.toUpperCase().replace(/\s+/g, '');
  if (compact === 'SINASIGNAR' || compact === 'SIN_ASIGNAR') return 'Sin Asignar';
  if (compact === 'B2B2C') return 'B2B2C';
  if (['B2B', 'B2C', 'B2G'].includes(compact)) return compact;
  if (ALLOWED_SEGMENTS.has(s)) return s;
  return s;
};

const isValidEmail = (value = '') =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || '').trim());

const isValidUrlish = (value = '') => {
  const v = String(value || '').trim();
  if (!v) return true;
  return /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-./?%&=]*)?$/i.test(v) || v.includes('linkedin.com');
};

/**
 * Teléfono de importación:
 * - celular: +56 9 XXXX XXXX
 * - fijo:    +56 2 XXXX XXXX (área 2–7)
 */
const isValidImportPhone = (value = '') => {
  const original = String(value || '');
  if (!original.trim()) return true;
  if (/&#|&nbsp;|&amp;|&lt;|&gt;|<|>/i.test(original)) return false;
  const raw = decodeBasicHtmlEntities(original).trim();
  if (!raw) return false;
  if (/^\+56 9 \d{4} \d{4}$/.test(raw)) return true;
  if (/^\+56 [2-7] \d{4} \d{4}$/.test(raw)) return true;
  return false;
};

const matchCatalogOption = (raw, options = [], { aliases = {} } = {}) => {
  const original = String(raw || '').trim();
  if (!original) return '';
  const key = foldCatalogKey(original);
  if (!key) return '';
  if (aliases[key]) return aliases[key];
  const list = (Array.isArray(options) ? options : [])
    .map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
    .filter((o) => o && (o.value || o.label));
  const exact = list.find(
    (o) => foldCatalogKey(o.value) === key || foldCatalogKey(o.label) === key
  );
  if (exact) return exact.value;
  const partial = list.find((o) => {
    const vk = foldCatalogKey(o.value);
    const lk = foldCatalogKey(o.label);
    return (
      (vk && (vk.includes(key) || key.includes(vk))) ||
      (lk && (lk.includes(key) || key.includes(lk)))
    );
  });
  if (partial) return partial.value;
  return original;
};

const optionValuesSet = (options = []) =>
  new Set(
    (Array.isArray(options) ? options : [])
      .map((o) => String(typeof o === 'string' ? o : o?.value || '').trim())
      .filter(Boolean)
  );

const homologateCreatedBy = (raw, executiveOptions = []) => {
  const original = String(raw || '').trim();
  if (!original) return '';
  const key = foldCatalogKey(original);
  const list = (executiveOptions || []).filter((o) => o && o.value !== '' && o.value != null);
  const byLabel = list.find((o) => foldCatalogKey(o.label) === key);
  if (byLabel) return String(byLabel.label || '').trim();
  const byValue = list.find((o) => foldCatalogKey(o.value) === key || String(o.value) === original);
  if (byValue) return String(byValue.label || byValue.value || '').trim();
  const partial = list.find((o) => {
    const lk = foldCatalogKey(o.label);
    return lk && (lk.includes(key) || key.includes(lk));
  });
  if (partial) return String(partial.label || '').trim();
  return original;
};

const homologateChannel = (raw, channelOptions = []) => {
  const parts = parseOrganizationChannels(raw);
  if (!parts.length) return '';
  const mapped = parts.map((part) => matchCatalogOption(part, channelOptions));
  return serializeOrganizationChannels(mapped);
};

const homologateCommuneRegion = (communeRaw, regionRaw) => {
  let commune = matchCatalogOption(communeRaw, ALL_COMMUNE_OPTIONS);
  let region = normalizeChileRegion(regionRaw);
  if (region && !chileRegions.some((r) => r.value === region)) {
    region = matchCatalogOption(regionRaw, chileRegions.map((r) => ({ value: r.value, label: r.label })));
    region = normalizeChileRegion(region) || region;
  }
  const communeRegion = COMMUNE_TO_REGION[foldCatalogKey(commune)] || '';
  if (commune && communeRegion) {
    region = communeRegion;
  } else if (region && commune) {
    const inRegion = (chileCommunes[region] || []).some(
      (c) => foldCatalogKey(c.value) === foldCatalogKey(commune)
    );
    if (!inRegion) {
      // Comuna no pertenece a la región: preferir región de la comuna si existe
      if (communeRegion) region = communeRegion;
    }
  }
  return { commune, region };
};

/** Homologa campos de catálogo al cargar el preview. */
export const homologateOrganizationImportRow = (row = {}, { executiveOptions = [], unit } = {}) => {
  const channelOptions = getActiveChannelOptions(undefined, unit);
  const industryOptions = getActiveIndustryOptions(undefined, unit);
  const next = { ...emptyRow(), ...row };
  next.segment = normalizeSegmentValue(next.segment) || matchCatalogOption(next.segment, ORGANIZATION_SEGMENT_OPTIONS);
  if (next.segment && !ALLOWED_SEGMENTS.has(next.segment)) {
    next.segment = matchCatalogOption(next.segment, ORGANIZATION_SEGMENT_OPTIONS) || next.segment;
  }
  next.industry = matchCatalogOption(next.industry, industryOptions, {
    aliases: INDUSTRY_ALIASES,
  });
  const subOptions = getActiveSubindustryOptions(next.industry, unit);
  next.subindustry = matchCatalogOption(next.subindustry, subOptions);
  next.channel = homologateChannel(next.channel, channelOptions);
  const loc = homologateCommuneRegion(next.commune, next.region);
  next.commune = loc.commune;
  next.region = loc.region;
  next.country = String(row.country || '').trim()
    ? matchCatalogOption(next.country, COUNTRY_OPTIONS)
    : unit === 'aditivos'
      ? ''
      : 'Chile';
  next.created_by = homologateCreatedBy(next.created_by, executiveOptions);
  return next;
};

const isInCatalog = (value, options) => {
  const v = String(value || '').trim();
  if (!v) return true;
  return optionValuesSet(options).has(v);
};

/** Devuelve mapa campo → mensaje de error (vacío = ok). */
export const getOrganizationImportFieldErrors = (row = {}, { executiveOptions = [], unit } = {}) => {
  const errors = {};
  const channelOptions = getActiveChannelOptions(undefined, unit);
  const industryOptions = getActiveIndustryOptions(undefined, unit);
  const name = String(row.org_name || '').trim();
  if (!name) {
    errors.org_name = 'Nombre obligatorio';
  } else if (/^[<>/?]/.test(name) || /<\/?[A-Za-z]/.test(name)) {
    errors.org_name = 'Nombre inválido';
  }

  const rut = String(row.rut || '').trim();
  if (rut) {
    if (!isRutDisplayFormat(rut)) {
      errors.rut = 'Formato: 12.345.678-9';
    } else if (!isValidChileanRut(rut)) {
      errors.rut = 'RUT inválido';
    }
  }

  const segment = String(row.segment || '').trim();
  if (segment && !ALLOWED_SEGMENTS.has(normalizeSegmentValue(segment))) {
    errors.segment = 'Fuera de catálogo';
  }

  const industry = String(row.industry || '').trim();
  if (industry && !isInCatalog(industry, industryOptions)) {
    errors.industry = 'Fuera de catálogo';
  }

  const subindustry = String(row.subindustry || '').trim();
  if (subindustry) {
    const allowed = getActiveSubindustryOptions(industry, unit);
    if (!isInCatalog(subindustry, allowed)) {
      errors.subindustry = 'Fuera de catálogo';
    }
  }

  const channel = String(row.channel || '').trim();
  if (channel) {
    const parts = parseOrganizationChannels(channel);
    const bad = parts.some((part) => !isInCatalog(part, channelOptions));
    if (bad) errors.channel = 'Fuera de catálogo';
  }

  const email = String(row.email || '').trim();
  if (email && !isValidEmail(email)) {
    errors.email = 'Email inválido';
  }

  const phone = String(row.phone || '').trim();
  if (phone && !isValidImportPhone(phone)) {
    errors.phone = 'Formato: +56 9 XXXX XXXX';
  }

  const commune = String(row.commune || '').trim();
  if (commune && !isInCatalog(commune, ALL_COMMUNE_OPTIONS)) {
    errors.commune = 'Fuera de catálogo';
  }

  const region = String(row.region || '').trim();
  if (region && !chileRegions.some((r) => r.value === region)) {
    errors.region = 'Fuera de catálogo';
  }

  const country = String(row.country || '').trim();
  if (country && !isInCatalog(country, COUNTRY_OPTIONS)) {
    errors.country = 'Fuera de catálogo';
  }

  const createdBy = String(row.created_by || '').trim();
  if (createdBy && executiveOptions?.length) {
    const labels = executiveOptions
      .filter((o) => o && o.value !== '' && o.value != null)
      .map((o) => ({ value: o.label, label: o.label }));
    if (!isInCatalog(createdBy, labels)) {
      errors.created_by = 'Fuera de catálogo';
    }
  }

  const website = String(row.website || '').trim();
  if (website && !isValidUrlish(website)) {
    errors.website = 'URL inválida';
  }

  const linkedin = String(row.linkedin || '').trim();
  if (linkedin && !isValidUrlish(linkedin)) {
    errors.linkedin = 'URL inválida';
  }

  return errors;
};

const OrganizationsBulkImportModal = ({
  isOpen,
  onClose,
  onImport,
  isImporting = false,
  isDarkMode = false,
  result = null,
  executiveOptions = [],
}) => {
  const unit = useBusinessUnit();
  const [step, setStep] = useState('source'); // source | preview
  const [rawText, setRawText] = useState('');
  const [previewRows, setPreviewRows] = useState([]);
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const parsed = useMemo(() => parseOrganizationsCsv(rawText), [rawText]);
  const channelOptions = useMemo(() => getActiveChannelOptions(undefined, unit), [unit]);

  useEffect(() => {
    if (!isOpen) {
      setStep('source');
      setRawText('');
      setPreviewRows([]);
      setLocalError('');
      setLocalSuccess('');
    }
  }, [isOpen]);

  const rowErrors = useMemo(
    () => previewRows.map((row) => getOrganizationImportFieldErrors(row, { executiveOptions, unit })),
    [previewRows, executiveOptions, unit]
  );

  const invalidFieldCount = useMemo(
    () => rowErrors.reduce((acc, errs) => acc + Object.keys(errs).length, 0),
    [rowErrors]
  );

  const invalidRowCount = useMemo(
    () => rowErrors.filter((errs) => Object.keys(errs).length > 0).length,
    [rowErrors]
  );

  if (!isOpen) return null;

  const colors = isDarkMode
    ? {
        overlay: 'rgba(0,0,0,0.6)',
        panel: '#18181b',
        border: '#3f3f46',
        text: '#f4f4f5',
        muted: '#a1a1aa',
        inputBg: '#09090b',
        inputBorder: '#52525b',
        tableHead: '#27272a',
        errorBg: 'rgba(220, 38, 38, 0.28)',
        errorBorder: '#f87171',
        errorText: '#fecaca',
        hintText: '#e7b0b0',
      }
    : {
        overlay: 'rgba(15,23,42,0.4)',
        panel: '#ffffff',
        border: '#e2e8f0',
        text: '#0f172a',
        muted: '#64748b',
        inputBg: '#ffffff',
        inputBorder: '#cbd5e1',
        tableHead: '#f1f5f9',
        errorBg: '#fee2e2',
        errorBorder: '#dc2626',
        errorText: '#991b1b',
        hintText: '#d4a5a5',
      };

  const goToPreview = (rows) => {
    setPreviewRows(
      rows.map((row) =>
        homologateOrganizationImportRow(
          { ...emptyRow(), ...row },
          { executiveOptions, unit }
        )
      )
    );
    setStep('preview');
    setLocalError('');
  };

  const getSelectOptionsForField = (field, row) => {
    if (field === 'segment') return ORGANIZATION_SEGMENT_OPTIONS;
    if (field === 'industry') return getActiveIndustryOptions(undefined, unit);
    if (field === 'subindustry') {
      return getActiveSubindustryOptions(row.industry, unit);
    }
    if (field === 'channel') return channelOptions;
    if (field === 'region') {
      return chileRegions.map((r) => ({ value: r.value, label: r.label }));
    }
    if (field === 'commune') {
      const region = String(row.region || '').trim();
      if (region && chileCommunes[region]) return chileCommunes[region];
      return ALL_COMMUNE_OPTIONS;
    }
    if (field === 'country') return COUNTRY_OPTIONS;
    if (field === 'created_by') {
      return (executiveOptions || [])
        .filter((o) => o && o.value !== '' && o.value != null)
        .map((o) => ({ value: o.label, label: o.label }));
    }
    return null;
  };

  const handleCellChange = (rowIndex, field, value) => {
    setPreviewRows((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIndex) return row;
        const next = { ...row, [field]: value };
        if (field === 'industry') {
          const allowed = getActiveSubindustryOptions(value, unit).map((o) => o.value);
          if (next.subindustry && !allowed.includes(next.subindustry)) {
            next.subindustry = '';
          }
        }
        if (field === 'commune') {
          const mappedRegion = COMMUNE_TO_REGION[foldCatalogKey(value)] || '';
          if (mappedRegion) next.region = mappedRegion;
        }
        if (field === 'region') {
          const communes = chileCommunes[value] || [];
          const ok = communes.some((c) => c.value === next.commune);
          if (next.commune && !ok) next.commune = '';
        }
        return next;
      })
    );
  };

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setRawText(text);
      setLocalError('');
      const next = parseOrganizationsCsv(text);
      if (next.error) {
        setLocalError(next.error);
        setStep('source');
        return;
      }
      goToPreview(next.rows);
    };
    reader.onerror = () => setLocalError('No se pudo leer el archivo.');
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleContinueFromSource = () => {
    setLocalError('');
    if (parsed.error) {
      setLocalError(parsed.error);
      return;
    }
    if (!parsed.rows.length) {
      setLocalError('No hay filas para importar.');
      return;
    }
    goToPreview(parsed.rows);
  };

  const handleRemoveRow = (rowIndex) => {
    setPreviewRows((prev) => prev.filter((_, idx) => idx !== rowIndex));
  };

  const handleSubmit = async () => {
    setLocalError('');
    setLocalSuccess('');
    if (!previewRows.length) {
      setLocalError('No hay filas para importar.');
      return;
    }
    if (invalidFieldCount > 0) {
      setLocalError(
        `Corrige ${invalidFieldCount} campo(s) en rojo (${invalidRowCount} fila(s)) antes de importar.`
      );
      return;
    }
    try {
      const data = await onImport(previewRows);
      const created = Number(data?.created || 0);
      const skipped = Number(data?.skipped || 0);
      const errors = Number(data?.errors || 0);
      if (errors > 0 && created === 0) {
        setLocalError(
          data?.error_rows?.[0]?.detail ||
            `La importación terminó con ${errors} error(es). Revisa el detalle.`
        );
        return;
      }
      setLocalSuccess(
        `Importación exitosa: ${created} creadas` +
          (skipped ? `, ${skipped} omitidas` : '') +
          (errors ? `, ${errors} con error` : '') +
          '.'
      );
      window.setTimeout(() => {
        onClose?.();
      }, 1100);
    } catch (err) {
      setLocalError(err?.message || 'No se pudo importar.');
    }
  };

  const panelWidth = step === 'preview' ? 'min(1180px, 98vw)' : 'min(720px, 100%)';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 12000,
        background: colors.overlay,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        style={{
          width: panelWidth,
          maxHeight: '92vh',
          overflow: 'auto',
          background: colors.panel,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0, color: colors.text, fontSize: '18px' }}>
              {step === 'preview' ? 'Revisar importación' : 'Carga masiva de organizaciones'}
            </h3>
            <p style={{ margin: '4px 0 0', color: colors.muted, fontSize: '13px' }}>
              {step === 'preview'
                ? 'Edita las celdas si hace falta. Catálogos se homologan al cargar; el formato incorrecto se marca en rojo.'
                : 'Sube la plantilla Excel (.xls) o un CSV (;). Luego revisas la tabla antes de importar.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting}
            style={{
              border: 'none',
              background: 'transparent',
              color: colors.muted,
              cursor: 'pointer',
              padding: 4,
            }}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {step === 'source' ? (
          <>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: colors.inputBg,
                  color: colors.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Upload size={15} />
                Subir CSV / plantilla .xls
                <input
                  type="file"
                  accept=".csv,.xls,text/csv,text/plain,application/vnd.ms-excel"
                  hidden
                  onChange={handleFile}
                />
              </label>
              <button
                type="button"
                onClick={downloadOrganizationsExcelTemplate}
                style={{
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 8,
                  border: `1px solid ${colors.inputBorder}`,
                  background: 'transparent',
                  color: colors.text,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Descargar plantilla Excel
              </button>
            </div>

            <textarea
              value={rawText}
              onChange={(e) => {
                setRawText(e.target.value);
                setLocalError('');
              }}
              placeholder={`org_name;rut;segment;channel;...\n${TEMPLATE_EXAMPLE}`}
              rows={10}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: 8,
                border: `1px solid ${colors.inputBorder}`,
                background: colors.inputBg,
                color: colors.text,
                padding: 10,
                fontSize: 12,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                resize: 'vertical',
              }}
            />

            <div style={{ fontSize: 12, color: colors.muted }}>
              {parsed.error
                ? parsed.error
                : `${parsed.rows.length} fila(s) detectadas. Continúa para revisarlas.`}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
                fontSize: 12,
                color: colors.muted,
              }}
            >
              <span>
                {previewRows.length} fila(s)
                {invalidFieldCount > 0
                  ? ` · ${invalidFieldCount} campo(s) con formato incorrecto`
                  : ' · formato OK'}
              </span>
              <button
                type="button"
                onClick={() => {
                  setStep('source');
                  setLocalError('');
                }}
                disabled={isImporting}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: colors.text,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 12,
                  textDecoration: 'underline',
                }}
              >
                Volver al archivo
              </button>
            </div>

            <div
              style={{
                overflow: 'auto',
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                maxHeight: '58vh',
              }}
            >
              <table
                style={{
                  borderCollapse: 'collapse',
                  width: '100%',
                  minWidth: 1100,
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr style={{ background: colors.tableHead, position: 'sticky', top: 0, zIndex: 1 }}>
                    <th
                      style={{
                        padding: '8px 6px',
                        textAlign: 'left',
                        color: colors.muted,
                        borderBottom: `1px solid ${colors.border}`,
                        width: 36,
                      }}
                    >
                      #
                    </th>
                    {TEMPLATE_HEADERS.map((field) => (
                      <th
                        key={field}
                        style={{
                          padding: '8px 6px',
                          textAlign: 'left',
                          color: colors.text,
                          borderBottom: `1px solid ${colors.border}`,
                          whiteSpace: 'nowrap',
                          fontWeight: 600,
                        }}
                      >
                        {COLUMN_LABELS[field] || field}
                      </th>
                    ))}
                    <th
                      style={{
                        padding: '8px 6px',
                        borderBottom: `1px solid ${colors.border}`,
                        width: 40,
                      }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rowIndex) => {
                    const errors = rowErrors[rowIndex] || {};
                    return (
                      <tr key={`preview-row-${rowIndex}`}>
                        <td
                          style={{
                            padding: '4px 6px',
                            color: colors.muted,
                            borderBottom: `1px solid ${colors.border}`,
                            verticalAlign: 'top',
                          }}
                        >
                          {rowIndex + 1}
                        </td>
                        {TEMPLATE_HEADERS.map((field) => {
                          const hasError = Boolean(errors[field]);
                          const selectOptions = getSelectOptionsForField(field, row);
                          const currentValue = row[field] || '';
                          const inputStyle = {
                            width: '100%',
                            boxSizing: 'border-box',
                            border: hasError
                              ? `2px solid ${colors.errorBorder}`
                              : `1px solid ${colors.inputBorder}`,
                            background: hasError ? colors.errorBg : colors.inputBg,
                            color: hasError ? colors.errorText : colors.text,
                            borderRadius: 4,
                            padding: '5px 6px',
                            fontSize: 12,
                            fontWeight: hasError ? 600 : 400,
                            outline: 'none',
                            boxShadow: hasError ? `0 0 0 1px ${colors.errorBorder}` : 'none',
                          };
                          const orphanOption =
                            selectOptions &&
                            currentValue &&
                            !selectOptions.some((o) => o.value === currentValue)
                              ? [{ value: currentValue, label: `${currentValue} (sin homologar)` }]
                              : [];
                          return (
                            <td
                              key={`${rowIndex}-${field}`}
                              style={{
                                padding: 2,
                                borderBottom: `1px solid ${colors.border}`,
                                verticalAlign: 'top',
                                minWidth: field === 'org_name' || field === 'address' ? 140 : 90,
                              }}
                            >
                              {selectOptions ? (
                                <select
                                  value={currentValue}
                                  onChange={(e) => handleCellChange(rowIndex, field, e.target.value)}
                                  title={hasError ? errors[field] : undefined}
                                  disabled={isImporting}
                                  style={inputStyle}
                                >
                                  <option value="">—</option>
                                  {[...orphanOption, ...selectOptions].map((opt) => (
                                    <option key={`${field}-${opt.value}`} value={opt.value}>
                                      {opt.label || opt.value}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  value={currentValue}
                                  onChange={(e) => handleCellChange(rowIndex, field, e.target.value)}
                                  title={hasError ? errors[field] : undefined}
                                  disabled={isImporting}
                                  style={inputStyle}
                                />
                              )}
                              {hasError ? (
                                <div
                                  style={{
                                    color: colors.hintText,
                                    fontSize: 9,
                                    marginTop: 2,
                                    lineHeight: 1.15,
                                    maxWidth: 140,
                                    fontWeight: 400,
                                  }}
                                >
                                  {errors[field]}
                                </div>
                              ) : null}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            padding: '4px 6px',
                            borderBottom: `1px solid ${colors.border}`,
                            verticalAlign: 'top',
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(rowIndex)}
                            disabled={isImporting}
                            title="Quitar fila"
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: colors.muted,
                              cursor: 'pointer',
                              padding: 4,
                              display: 'inline-flex',
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {(localError || localSuccess || result) && (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              fontSize: 13,
              color: colors.text,
              background: isDarkMode ? '#09090b' : '#f8fafc',
            }}
          >
            {localSuccess ? (
              <div style={{ color: isDarkMode ? '#86efac' : '#15803d', fontWeight: 600 }}>
                {localSuccess}
              </div>
            ) : null}
            {localError ? <div style={{ color: '#dc2626' }}>{localError}</div> : null}
            {!localSuccess && result ? (
              <div style={{ display: 'grid', gap: 4 }}>
                <div>
                  Creadas: <strong>{result.created || 0}</strong> · Omitidas:{' '}
                  <strong>{result.skipped || 0}</strong> · Errores:{' '}
                  <strong>{result.errors || 0}</strong>
                </div>
                {Array.isArray(result.error_rows) && result.error_rows.length > 0 ? (
                  <div style={{ color: '#dc2626', fontSize: 12 }}>
                    {result.error_rows.slice(0, 5).map((row) => (
                      <div key={`err-${row.row}`}>
                        Fila {row.row}: {row.detail}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={isImporting}
            style={{
              height: 38,
              padding: '0 14px',
              borderRadius: 8,
              border: `1px solid ${colors.inputBorder}`,
              background: 'transparent',
              color: colors.text,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cerrar
          </button>
          {step === 'source' ? (
            <button
              type="button"
              onClick={handleContinueFromSource}
              disabled={Boolean(parsed.error) || !parsed.rows.length}
              style={{
                height: 38,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: !parsed.rows.length || parsed.error ? '#94a3b8' : '#2563eb',
                color: '#fff',
                fontWeight: 600,
                cursor: !parsed.rows.length || parsed.error ? 'not-allowed' : 'pointer',
              }}
            >
              Revisar datos
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
            disabled={isImporting || Boolean(localSuccess)}
            style={{
              height: 38,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background:
                isImporting || !previewRows.length || invalidFieldCount > 0 || localSuccess
                  ? '#86efac'
                  : '#16a34a',
              color: '#fff',
              fontWeight: 600,
              cursor:
                isImporting || !previewRows.length || invalidFieldCount > 0 || localSuccess
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            {isImporting ? 'Importando…' : localSuccess ? 'Listo' : `Importar ${previewRows.length || ''}`}
          </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationsBulkImportModal;
